/**
 * Custom Refine access-control provider for the AOS portal.
 *
 * Backend RBAC is the single source of truth (frontend.spec §10). This provider
 * only hides/disables UI: it asks the worker `POST /api/access/can`
 * (`accessCanSchema` body `{ resource, action, recordId? }`) and maps the
 * worker's `{ allowed, reason }` onto Refine's `{ can, reason }`. The backend
 * still enforces every mutation, so a failure here fails closed (deny) without
 * blocking the security boundary.
 */

import type { AccessControlProvider } from '@refinedev/core';
import type { AccessCanInput } from '@aos/shared';

import { api } from '@/lib/api';

/** Worker response shape for `POST /api/access/can` (CONTRACT §7). */
interface CanResponseBody {
  allowed: boolean;
  reason?: string;
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action, params }) => {
    // Refine may invoke `can` before a resource is known (e.g. global buttons);
    // without a resource there is nothing to authorize, so allow and let the
    // backend enforce on the actual request.
    if (!resource) {
      return { can: true };
    }

    const body: AccessCanInput = {
      resource,
      action: action as AccessCanInput['action'],
      recordId: params?.id !== undefined ? String(params.id) : undefined,
    };

    try {
      const result = await api.post<CanResponseBody>('/access/can', body);
      return { can: result.allowed, reason: result.reason };
    } catch {
      // Fail closed for UI visibility — hide the control if we cannot confirm
      // access. The backend remains the authoritative gate on the real action.
      return { can: false, reason: 'Access check failed' };
    }
  },
  options: {
    buttons: { enableAccessControl: true, hideIfUnauthorized: false },
  },
};

export default accessControlProvider;
